'use client';

import { c as _c } from "react/compiler-runtime";
import type React from 'react';
import { usePathname } from 'next/navigation';
import { useState, useEffect, useMemo } from 'react';
import { Drawer } from 'src/components/Molecules/Drawer';
import { List } from 'src/components/Molecules/List';
import { BrandBox, BrandImage } from 'src/components/Organisms/Brand';
import { BRAND_PRIMARY, BRAND_SYMBOL } from 'src/constants/styles';
import { ROUTES } from 'src/constants/routes';
import useMenuItems from 'src/hooks/useMenuItems';
import useReports from 'src/hooks/useReports';
import useRouteWithParams from 'src/hooks/useRouteWithParams';
import { styled } from '@mui/material/styles';
import AccountMenu from './AccountMenu';
import SidebarItem from './SidebarItem';
// TODO: pull these routes from a config file or API

export interface SidebarProps {
  currentPath?: string;
  open?: boolean;
  disabled?: boolean;
  handleDrawerAction: (open: boolean) => void;
}
const AnimatedBrandImage = styled(BrandImage)(({
  theme
}) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['opacity'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.standard
  }),
  variants: [{
    props: ({
      small
    }) => !small,
    style: {
      width: `${BRAND_PRIMARY.width.rem}rem`,
      height: `${BRAND_PRIMARY.height.rem}rem`
    }
  }, {
    props: ({
      small
    }) => small,
    style: {
      width: `${BRAND_SYMBOL.width.rem}rem`,
      height: `${BRAND_SYMBOL.height.rem}rem`
    }
  }]
}));
const AnimatedBrand = styled(BrandBox)(({
  theme
}) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['opacity, width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.standard
  }),
  variants: [{
    props: ({
      small
    }) => !small,
    style: {
      padding: `${theme.sizes.spacing.none} ${theme.sizes.spacing['07']}`,
      height: '2rem'
    }
  }, {
    props: ({
      small
    }) => small,
    style: {
      padding: `${theme.sizes.spacing.none} ${theme.sizes.spacing['07']}`,
      height: '2rem'
    }
  }]
}));
const Divider = styled('hr')(({
  theme
}) => ({
  border: '0',
  borderTop: `1px solid ${theme.colors.border.hover}`,
  margin: theme.sizes.spacing['04']
}));

// Kept as its own component so the menu/report hooks below only run when the
// sidebar is actually interactive. Shared reports render a disabled sidebar and
// have no team or feature flags, so building the menu there is pure waste.
const SidebarItems: React.FC<{
  open: boolean;
  handleDrawerAction: (open: boolean) => void;
}> = t0 => {
  const $ = _c(19);
  const {
    open,
    handleDrawerAction
  } = t0;
  const {
    updatePathWithParams
  } = useRouteWithParams();
  const pathname = usePathname();
  const {
    reportList
  } = useReports();
  const t1 = !!reportList && reportList.length > 0;
  let t2;
  if ($[0] !== pathname || $[1] !== reportList || $[2] !== updatePathWithParams) {
    let t3;
    if ($[4] !== pathname || $[5] !== updatePathWithParams) {
      t3 = report => ({
        path: updatePathWithParams(ROUTES.DASHBOARD_DETAIL, {
          reportId: report.id
        }),
        name: report.name,
        isActive: pathname === updatePathWithParams(ROUTES.DASHBOARD_DETAIL, {
          reportId: report.id
        })
      });
      $[4] = pathname;
      $[5] = updatePathWithParams;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    t2 = reportList.map(t3);
    $[0] = pathname;
    $[1] = reportList;
    $[2] = updatePathWithParams;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = {
      [ROUTES.DASHBOARDS]: {
        enabled: t1,
        children: t2
      }
    };
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const itemsOverrides = t3;
  const {
    menuItems
  } = useMenuItems(itemsOverrides);
  let t4;
  if ($[10] !== handleDrawerAction || $[11] !== menuItems || $[12] !== open) {
    let t5;
    if ($[14] !== handleDrawerAction || $[15] !== open) {
      t5 = (route, idx) => {
        if (route.enabled && route.type !== "divider") {
          return <SidebarItem iconOnly={!open} route={route} key={`menu-item-${route.path}-${route.name}`} isActive={route.isActive} expandSidebar={() => handleDrawerAction(true)} />;
        } else {
          if (route.type === "divider") {
            return <Divider key={`divider-${idx}`} />;
          }
        }
        return null;
      };
      $[14] = handleDrawerAction;
      $[15] = open;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    t4 = menuItems.map(t5);
    $[10] = handleDrawerAction;
    $[11] = menuItems;
    $[12] = open;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[17] !== t4) {
    t5 = <>{t4}</>;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
};
const Sidebar: React.FC<SidebarProps> = t0 => {
  const $ = _c(24);
  const {
    open: t1,
    disabled: t2,
    handleDrawerAction
  } = t0;
  const open = t1 === undefined ? true : t1;
  const disabled = t2 === undefined ? false : t2;
  const [isClient, setIsClient] = useState(false);
  let t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setIsClient(true);
    };
    t4 = [];
    $[0] = t3;
    $[1] = t4;
  } else {
    t3 = $[0];
    t4 = $[1];
  }
  useEffect(t3, t4);
  const t5 = !open;
  const t6 = !open;
  let t7;
  if ($[2] !== t6) {
    t7 = <AnimatedBrandImage small={t6} alt="Brand Logo" />;
    $[2] = t6;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  let t8;
  if ($[4] !== t5 || $[5] !== t7) {
    t8 = <AnimatedBrand small={t5}>{t7}</AnimatedBrand>;
    $[4] = t5;
    $[5] = t7;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== disabled || $[8] !== handleDrawerAction || $[9] !== isClient || $[10] !== open) {
    t9 = isClient && !disabled && <SidebarItems open={open} handleDrawerAction={handleDrawerAction} />;
    $[7] = disabled;
    $[8] = handleDrawerAction;
    $[9] = isClient;
    $[10] = open;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== open || $[13] !== t9) {
    t10 = <List className="inverted rootList" withScroll={open} vertical={true}>{t9}</List>;
    $[12] = open;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== disabled || $[16] !== handleDrawerAction || $[17] !== open) {
    t11 = !disabled && <AccountMenu isExpanded={open} expandSidebar={() => handleDrawerAction(true)} />;
    $[15] = disabled;
    $[16] = handleDrawerAction;
    $[17] = open;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== open || $[20] !== t10 || $[21] !== t11 || $[22] !== t8) {
    t12 = <Drawer open={open} screenHeight={true} withScroll={false}>{t8}{t10}{t11}</Drawer>;
    $[19] = open;
    $[20] = t10;
    $[21] = t11;
    $[22] = t8;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  return t12;
};
export default Sidebar;
export const dynamic = 'force-dynamic';